import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, FolderOpen, Trash, ArrowRight, Stack, Users, Tag, MicrophoneStage, UsersFour } from "@phosphor-icons/react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import ShareProjectModal from "@/components/ShareProjectModal";
import { api, formatErr } from "@/lib/api";
import { LANGUAGES, DIALECTS, langLabel } from "@/lib/languages";

const ENGINE_LABELS = {
  whisper: "Flowscribe-WEngine",
  elevenlabs: "Flowscribe-EEngine",
  google: "Flowscribe-GEngine",
  azure: "Flowscribe-AEngine",
};

export default function Dashboard() {
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [showBatch, setShowBatch] = useState(false);
  const [scope, setScope] = useState("all"); // all | owned | shared
  const [activeTag, setActiveTag] = useState("");
  const [activeEngine, setActiveEngine] = useState("");
  const [shareProject, setShareProject] = useState(null);

  const onShareChanged = useCallback((projectId, partial) => {
    setProjects((list) => list.map((p) => (
      p.project_id === projectId
        ? { ...p, shared_count: (partial.shared_with || []).length }
        : p
    )));
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const allTags = useMemo(() => {
    const s = new Set();
    projects.forEach((p) => (p.tags || []).forEach((t) => s.add(t)));
    return Array.from(s).sort();
  }, [projects]);

  const allEngines = useMemo(() => {
    const s = new Set();
    projects.forEach((p) => p.stt_engine && s.add(p.stt_engine));
    return Array.from(s).sort();
  }, [projects]);

  const filtered = useMemo(() => {
    let arr = projects;
    if (scope === "owned") arr = arr.filter((p) => p.owned);
    else if (scope === "shared") arr = arr.filter((p) => !p.owned);
    if (activeTag) arr = arr.filter((p) => (p.tags || []).includes(activeTag));
    if (activeEngine) arr = arr.filter((p) => p.stt_engine === activeEngine);
    return arr;
  }, [projects, scope, activeTag, activeEngine]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this project? This cannot be undone.")) return;
    try {
      await api.delete(`/projects/${id}`);
      setProjects((p) => p.filter((x) => x.project_id !== id));
      toast.success("Project deleted");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <AppShell>
      <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-6 mb-10">
        <div>
          <div className="overline text-[--muted] mb-3">YOUR PROJECTS</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">Studio</h1>
          <p className="mt-3 text-[--muted] max-w-xl">
            Each project pairs an audio file with a transcribed SRT and any translations or dialect
            conversions you've generated.
          </p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          data-testid="new-project-btn"
          className="bg-[--accent] text-white px-5 py-3 text-sm font-bold inline-flex items-center gap-2 hover:bg-[--accent-hi] transition-colors"
        >
          <Plus weight="bold" size={16} /> New project
        </button>
      </div>

      <div className="mb-6 -mt-4 flex items-center gap-3 flex-wrap">
        <button
          onClick={() => setShowBatch(true)}
          data-testid="batch-upload-btn"
          className="border border-[--ink] px-4 py-2 text-xs font-bold inline-flex items-center gap-2 hover:bg-[--ink] hover:text-white transition-colors"
        >
          <Stack weight="bold" size={14} /> Batch upload
        </button>
        <div className="inline-flex border border-[--ink]" data-testid="dashboard-scope-tabs">
          {[
            { id: "all", label: "All" },
            { id: "owned", label: "Mine" },
            { id: "shared", label: "Shared with me" },
          ].map((s, i) => (
            <button
              key={s.id}
              onClick={() => setScope(s.id)}
              data-testid={`scope-${s.id}-btn`}
              className={`px-3 py-2 text-xs font-bold ${i > 0 ? "border-l border-[--ink]" : ""} ${scope === s.id ? "bg-[--ink] text-white" : "hover:bg-[--paper]"}`}
            >
              {s.label}
            </button>
          ))}
        </div>
        {allTags.length > 0 && (
          <div className="inline-flex items-center gap-2 flex-wrap">
            <Tag weight="bold" size={12} className="text-[--muted]" />
            <button
              onClick={() => setActiveTag("")}
              data-testid="tag-all-btn"
              className={`px-2 py-1 text-[11px] font-mono border border-[--line] ${!activeTag ? "bg-[--ink] text-white border-[--ink]" : "hover:bg-[--paper]"}`}
            >
              all tags
            </button>
            {allTags.map((t) => (
              <button
                key={t}
                onClick={() => setActiveTag(t)}
                data-testid="tag-filter-btn"
                className={`px-2 py-1 text-[11px] font-mono border border-[--line] ${activeTag === t ? "bg-[--ink] text-white border-[--ink]" : "hover:bg-[--paper]"}`}
              >
                {t}
              </button>
            ))}
          </div>
        )}
        {allEngines.length > 1 && (
          <div className="inline-flex items-center gap-2 flex-wrap" data-testid="engine-filter-row">
            <MicrophoneStage weight="bold" size={12} className="text-[--muted]" />
            <button
              onClick={() => setActiveEngine("")}
              data-testid="engine-all-btn"
              className={`px-2 py-1 text-[11px] font-mono border border-[--line] ${!activeEngine ? "bg-[--ink] text-white border-[--ink]" : "hover:bg-[--paper]"}`}
            >
              all engines
            </button>
            {allEngines.map((e) => (
              <button
                key={e}
                onClick={() => setActiveEngine(e)}
                data-testid={`engine-filter-${e}`}
                className={`px-2 py-1 text-[11px] font-mono border border-[--line] ${activeEngine === e ? "bg-[--ink] text-white border-[--ink]" : "hover:bg-[--paper]"}`}
              >
                {ENGINE_LABELS[e] || e}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div className="overline text-[--muted]">Loading…</div>
      ) : filtered.length === 0 ? (
        projects.length === 0 ? (
          <EmptyState onCreate={() => setShowNew(true)} />
        ) : (
          <div className="hard-border bg-white p-10 text-center text-sm text-[--muted]">
            No projects match the current filters.
          </div>
        )
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[--line] border border-[--line]">
          {filtered.map((p) => (
            <ProjectCard key={p.project_id} p={p} onOpen={() => nav(`/project/${p.project_id}`)} onDelete={() => onDelete(p.project_id)} onShare={() => setShareProject(p)} />
          ))}
        </div>
      )}

      {showNew && <NewProjectModal onClose={() => setShowNew(false)} onCreated={(p) => nav(`/project/${p.project_id}`)} />}
      {showBatch && <BatchUploadModal onClose={() => setShowBatch(false)} onCreated={async () => { await load(); setShowBatch(false); }} />}
      {shareProject && (
        <ShareProjectModal
          project={shareProject}
          onClose={() => setShareProject(null)}
          onChanged={(partial) => onShareChanged(shareProject.project_id, partial)}
        />
      )}
    </AppShell>
  );
}

function ProjectCard({ p, onOpen, onDelete, onShare }) {
  return (
    <div className="group bg-white p-6 hover:bg-[--paper] transition-colors relative">
      <div className="flex items-center justify-between mb-4">
        <span className="overline text-[--muted]">{p.status === "ready" ? "READY" : p.status.toUpperCase()}</span>
        <div className="flex items-center gap-2">
          {!p.owned && (
            <span className="text-[10px] font-mono px-1.5 py-0.5 bg-[--paper] border border-[--line] inline-flex items-center gap-1" data-testid="project-shared-badge" title={p.owner ? `Shared by ${p.owner.email}` : "Shared with you"}>
              <Users weight="bold" size={10} /> SHARED
            </span>
          )}
          {p.owned && p.shared_count > 0 && (
            <span className="text-[10px] font-mono px-1.5 py-0.5 bg-[--paper] border border-[--line] inline-flex items-center gap-1" title={`Shared with ${p.shared_count} user(s)`}>
              <Users weight="bold" size={10} /> {p.shared_count}
            </span>
          )}
          <StatusDot status={p.status} />
        </div>
      </div>
      <h3 className="font-display text-xl font-bold tracking-tight mb-2 line-clamp-2" data-testid="project-card-title">{p.title}</h3>
      <div className="font-mono text-xs text-[--muted]">
        {langLabel(p.source_language)}
        {p.source_dialect ? ` · ${p.source_dialect}` : ""}
      </div>
      <div className="font-mono text-xs text-[--muted] mt-1 flex items-center gap-2 flex-wrap">
        <span data-testid="project-card-tracks">{p.tracks_count || 0} file{p.tracks_count === 1 ? "" : "s"}</span>
        <span>·</span>
        <span>{p.translations_count} translation{p.translations_count === 1 ? "" : "s"}</span>
        {p.stt_engine && (
          <span className="px-1.5 py-0.5 border border-[--line]" data-testid="project-card-engine">{(ENGINE_LABELS[p.stt_engine] || p.stt_engine).toUpperCase()}</span>
        )}
        {!p.owned && p.owner && <span>· by {p.owner.name || p.owner.email}</span>}
      </div>
      {p.tags?.length > 0 && (
        <div className="mt-2 flex gap-1 flex-wrap">
          {p.tags.map((t) => (
            <span key={t} className="text-[10px] font-mono px-1.5 py-0.5 bg-[--paper] border border-[--line]" data-testid="project-tag-chip">{t}</span>
          ))}
        </div>
      )}
      <div className="mt-6 flex items-center justify-between">
        <button
          onClick={onOpen}
          data-testid={`project-card-open-${p.project_id}`}
          className="inline-flex items-center gap-2 text-sm font-semibold hover:text-[--accent]"
        >
          Open <ArrowRight weight="bold" size={14} />
        </button>
        <div className="flex items-center gap-1">
          {p.owned && (
            <button
              onClick={(e) => { e.stopPropagation(); onShare?.(); }}
              data-testid={`project-card-share-${p.project_id}`}
              className="opacity-0 group-hover:opacity-100 transition-opacity p-2 hover:text-[--accent]"
              aria-label="Share project"
              title="Share"
            >
              <UsersFour weight="bold" size={16} />
            </button>
          )}
          {p.owned && (
            <button
              onClick={onDelete}
              data-testid="delete-project-btn"
              className="opacity-0 group-hover:opacity-100 transition-opacity p-2 hover:text-[--accent]"
              aria-label="Delete project"
            >
              <Trash weight="bold" size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusDot({ status }) {
  const map = {
    ready: "bg-emerald-500",
    transcribing: "bg-amber-500 animate-pulse",
    failed: "bg-red-500",
    partial: "bg-sky-500",
    draft: "bg-[--muted]",
  };
  return <span className={`w-2 h-2 ${map[status] || map.draft}`}></span>;
}

function EmptyState({ onCreate }) {
  return (
    <div className="hard-border bg-white p-14 grid place-items-center text-center">
      <FolderOpen weight="duotone" size={56} className="text-[--ink] mb-4" />
      <h3 className="font-display text-2xl font-bold tracking-tight">No projects yet</h3>
      <p className="text-sm text-[--muted] mt-2 mb-6 max-w-md">
        Create a project to upload audio, generate an SRT, and translate across languages or dialects.
      </p>
      <button
        onClick={onCreate}
        data-testid="empty-create-btn"
        className="bg-[--ink] text-white px-5 py-3 text-sm font-bold inline-flex items-center gap-2 hover:bg-[--accent] transition-colors"
      >
        <Plus weight="bold" size={16} /> Create your first project
      </button>
    </div>
  );
}

function NewProjectModal({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [lang, setLang] = useState("ar");
  const [dialect, setDialect] = useState("");
  const [engine, setEngine] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings");
        setSettings(data);
        setEngine(data.default_stt_engine);
      } catch (err) { console.warn("[dashboard] failed to fetch settings", err); }
    })();
  }, []);

  const dialectOptions = DIALECTS[lang] || [];

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const tagList = tagsInput
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
      const { data } = await api.post("/projects", {
        title: title.trim() || "Untitled project",
        source_language: lang,
        source_dialect: dialect || null,
        stt_engine: engine || null,
        tags: tagList,
      });
      toast.success("Project created");
      onCreated(data);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[--ink]/60 backdrop-blur-sm grid place-items-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white hard-border max-w-lg w-full p-8"
        onClick={(e) => e.stopPropagation()}
        data-testid="new-project-modal"
      >
        <div className="overline text-[--muted] mb-2">NEW PROJECT</div>
        <h2 className="font-display font-black text-3xl tracking-tighter mb-6">Name your project.</h2>
        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="overline block mb-2">Title</label>
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Podcast — Ep. 042"
              data-testid="project-title-input"
              className="w-full border border-[--ink] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[--accent]"
            />
          </div>
          <div>
            <label className="overline block mb-2">Source language (speech)</label>
            <select
              value={lang}
              onChange={(e) => { setLang(e.target.value); setDialect(""); }}
              data-testid="source-language-select"
              className="w-full border border-[--ink] px-4 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[--accent]"
            >
              {LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>{l.name} — {l.native}</option>
              ))}
            </select>
          </div>
          {dialectOptions.length > 0 && (
            <div>
              <label className="overline block mb-2">Source dialect (optional)</label>
              <select
                value={dialect}
                onChange={(e) => setDialect(e.target.value)}
                data-testid="source-dialect-select"
                className="w-full border border-[--ink] px-4 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[--accent]"
              >
                <option value="">— none / mixed —</option>
                {dialectOptions.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          )}
          {settings?.engines && (
            <div>
              <label className="overline block mb-2">STT engine</label>
              <select
                value={engine}
                onChange={(e) => setEngine(e.target.value)}
                data-testid="project-engine-select"
                className="w-full border border-[--ink] px-4 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[--accent]"
              >
                {settings.engines.map((e) => (
                  <option key={e.id} value={e.id} disabled={!e.available}>
                    {e.name}{e.available ? "" : " — needs API key"}{e.id === settings.default_stt_engine ? " (default)" : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="overline block mb-2">Tags (optional, comma-separated)</label>
            <input
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="podcast, season-2, marketing"
              data-testid="project-tags-input"
              className="w-full border border-[--ink] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[--accent]"
            />
          </div>
          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-3 text-sm font-semibold hover:text-[--accent]">Cancel</button>
            <button
              type="submit"
              disabled={busy}
              data-testid="create-project-submit"
              className="bg-[--accent] text-white px-5 py-3 text-sm font-bold hover:bg-[--accent-hi] transition-colors disabled:opacity-50"
            >
              {busy ? "Creating…" : "Create project"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BatchUploadModal({ onClose, onCreated }) {
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [lang, setLang] = useState("ar");
  const [dialect, setDialect] = useState("");
  const [busy, setBusy] = useState(false);
  const dialectOptions = DIALECTS[lang] || [];

  const pick = (fs) => setFiles(Array.from(fs || []));

  const submit = async (e) => {
    e.preventDefault();
    if (!files.length) { toast.error("Add at least one audio file"); return; }
    setBusy(true);
    try {
      const form = new FormData();
      files.forEach((f) => form.append("files", f));
      form.append("source_language", lang);
      if (dialect) form.append("source_dialect", dialect);
      const { data } = await api.post("/projects/batch", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60 * 1000,
      });
      toast.success(`${data.projects.length} project${data.projects.length === 1 ? "" : "s"} queued`);
      await onCreated();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[--ink]/60 backdrop-blur-sm grid place-items-center z-50 p-4" onClick={onClose}>
      <div className="bg-white hard-border max-w-xl w-full p-8" onClick={(e) => e.stopPropagation()} data-testid="batch-upload-modal">
        <div className="overline text-[--muted] mb-2">BATCH UPLOAD</div>
        <h2 className="font-display font-black text-3xl tracking-tighter mb-6">One project per file.</h2>
        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="overline block mb-2">Source language</label>
            <select value={lang} onChange={(e) => { setLang(e.target.value); setDialect(""); }} data-testid="batch-language-select" className="w-full border border-[--ink] px-4 py-3 text-sm bg-white">
              {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.name} — {l.native}</option>)}
            </select>
          </div>
          {dialectOptions.length > 0 && (
            <div>
              <label className="overline block mb-2">Source dialect (optional)</label>
              <select value={dialect} onChange={(e) => setDialect(e.target.value)} data-testid="batch-dialect-select" className="w-full border border-[--ink] px-4 py-3 text-sm bg-white">
                <option value="">— none / mixed —</option>
                {dialectOptions.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="overline block mb-2">Audio files</label>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept=".mp3,.wav,.m4a,.mp4,.webm,.mpeg,.mpga,audio/*"
              onChange={(e) => pick(e.target.files)}
              data-testid="batch-files-input"
              className="block w-full text-sm font-mono border border-[--ink] p-3 bg-[--paper] file:mr-4 file:px-4 file:py-2 file:border file:border-[--ink] file:bg-white file:font-bold file:hover:bg-[--ink] file:hover:text-white file:cursor-pointer"
            />
            {files.length > 0 && (
              <ul className="mt-3 space-y-1 max-h-40 overflow-y-auto scroll-thin font-mono text-xs text-[--muted]">
                {files.map((f, i) => (
                  <li key={`${f.name}-${f.size}-${i}`} className="flex justify-between border-b border-dashed border-[--line] py-1">
                    <span className="truncate pr-2">{f.name}</span>
                    <span>{(f.size / (1024 * 1024)).toFixed(1)} MB</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-3 text-sm font-semibold hover:text-[--accent]">Cancel</button>
            <button type="submit" disabled={busy || !files.length} data-testid="batch-submit-btn" className="bg-[--accent] text-white px-5 py-3 text-sm font-bold hover:bg-[--accent-hi] transition-colors disabled:opacity-50">
              {busy ? "Uploading…" : `Queue ${files.length || 0} project${files.length === 1 ? "" : "s"}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
